<template>
  <div class="vehicle-marker">
    <!-- Show loading state while fetching settings -->
    <div v-if="isLoadingSetting" class="loading-indicator">
      <div class="spinner"></div>
    </div>

    <!-- Vehicle marker content -->
    <template v-else>
      <img
        v-if="!usingArrow"
        :src="vehicleIcon"
        :alt="vehicle.vehicle.licensePlate"
        class="vehicle-icon"
        :style="vehicleIconStyle"
      />
      <div v-else class="arrow-icon" :style="arrowIconStyle">
        {{ arrowSymbol }}
      </div>
      <div v-if="isLicensePlateVisible" class="vehicle-info" :class="{ arrow: usingArrow }">
        <McBadge :text="badgeText" variant="primary" type="button" />
      </div>
    </template>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import McBadge from '@/components/McBadge'
import { mapGetters } from 'vuex'

export default {
  name: 'VehicleMarker',
  components: {
    McBadge,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    vehicle: {
      type: Object,
      required: true,
    },
  },
  computed: {
    google: gmapApi,
    ...mapGetters('route', ['isLoadingSetting', 'usingArrow', 'isLicensePlateVisible']),
    normalizedDirection() {
      const direction = this.vehicle.direction || 0
      return ((direction % 360) + 360) % 360
    },
    googleMapsRotation() {
      return this.normalizedDirection - 90
    },
    vehicleIcon() {
      const icon = this.vehicle?.vehicle?.vehicleType?.icon || 'car'
      return require(`@/assets/images/vehicles/${icon}/${icon}-green.png`)
    },
    badgeText() {
      const licensePlate = this.vehicle.vehicle.licensePlate || ''
      const hullNo = this.vehicle.vehicle.hullNo || ''

      if (hullNo) {
        return `${licensePlate} (${hullNo})`
      }

      return licensePlate
    },
    arrowSymbol() {
      return '➤'
    },
    vehicleIconStyle() {
      return {
        transform: `rotate(${this.normalizedDirection}deg)`,
        transformOrigin: 'center center',
      }
    },
    arrowIconStyle() {
      return {
        transform: `rotate(${this.googleMapsRotation}deg)`,
        transformOrigin: 'center center',
        fontSize: '32px',
        color: '#137c55',
        fontWeight: 'bold',
        textShadow: '0 0 3px rgba(0,0,0,0.5)',
      }
    },
    getArrowSymbol() {
      if (!this.google || !this.google.maps) return null

      return {
        path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 4,
        fillColor: '#137c55',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 1,
        rotation: this.normalizedDirection,
      }
    },
  },
  methods: {
    createArrowMarker(map, position) {
      if (!this.google || !this.google.maps) return null

      return new this.google.maps.Marker({
        position: position,
        map: map,
        icon: this.getArrowSymbol,
        title: this.badgeText,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.vehicle-marker {
  position: absolute;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(40px);
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #137c55;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.vehicle-icon {
  width: 64px;
  height: 64px;
  object-fit: contain;
}

.arrow-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vehicle-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  &.arrow {
    margin-top: -15px;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .vehicle-marker {
    transform: translateY(40px);
  }

  .vehicle-icon {
    width: 64px;
    height: 64px;
  }

  .arrow-icon {
    width: 64px;
    height: 64px;
  }

  .vehicle-info {
    gap: 1px;

    &.arrow {
      margin-top: -5px;
    }
  }
}
</style>
